import { useCallback, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import { PaymentsDataCollectionDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { TransferHistorySidePanel } from './TransferHistorySidePanel'
import { mapFiltersToVariables } from './filters'
import type { Payment, PaymentBase } from './types'
import { usePaymentItemActions } from './usePaymentItemActions'
import type { MappedTransferChild } from './usePaymentNestedTransfers'
import { usePaymentNestedTransfers } from './usePaymentNestedTransfers'
import { usePaymentsDataCollection } from './usePaymentsDataCollection'
import { usePaymentsFilters } from './usePaymentsFilters'

interface Props {
  companyId: number
  locale: string
  canSeeSepaTransferAction: boolean
  onNewPaymentAction: () => void
}

export const PaymentsDataCollection = ({
  companyId: _companyId,
  locale,
  canSeeSepaTransferAction,
  onNewPaymentAction,
}: Props) => {
  const [selectedTransfer, setSelectedTransfer] = useState<MappedTransferChild | null>(null)

  const filterDefinitions = usePaymentsFilters()
  const { sortings, defaultSortings, columns } = usePaymentsDataCollection({
    locale,
  })
  const { getItemActions: getPaymentItemActions } = usePaymentItemActions({
    canSeeSepaTransferAction,
  })
  const { itemsWithChildren, childrenCount, fetchChildren } = usePaymentNestedTransfers()

  /**
   * Type guard to check if an item is a MappedTransferChild
   */
  const isMappedTransferChild = (item: Payment): item is MappedTransferChild => {
    return '_isTransfer' in item && item._isTransfer === true
  }

  // Combined item actions for both payments and transfers
  const getItemActions = useCallback(
    (item: PaymentBase) => {
      // Check if item is a transfer (child) - no actions for transfers
      // Since baseSource only returns PaymentBase, we don't need to check for _isTransfer here
      return getPaymentItemActions(item)
    },
    [getPaymentItemActions]
  )

  const onRowClick = useCallback(
    (item: PaymentBase): (() => void) => {
      return () => {
        // Check if item is a transfer (from nested table)
        // OneDataCollection passes transfers as children, so we need to check the actual item
        // Since itemOnClick receives PaymentBase from the source, transfers come through
        // the nested table system and may be passed here as well
        const paymentItem: Payment = item
        if (isMappedTransferChild(paymentItem)) {
          // It's a transfer - open the sidepanel
          setSelectedTransfer(paymentItem)
        }
        // No action for payment rows
      }
    },
    []
  )

  const baseSource = useGraphqlDataCollectionSource({
    document: PaymentsDataCollectionDocument,
    mapData: (data) => data.banking.paymentsConnection.edges.map((edge) => edge.node),
    mapVariables: ({ search, sortings: mapSortings, filters }) => {
      const mappedFilters = mapFiltersToVariables(filters)

      return {
        sortOrder: mapSortings?.[0],
        search,
        ...mappedFilters,
      }
    },
    paginationType: 'infinite-scroll',
    perPage: 25,
    mapPagination: (data) => ({
      total: data.banking.paymentsConnection.totalCount ?? 0,
      cursor: data.banking.paymentsConnection.pageInfo.endCursor ?? undefined,
      hasNextPage: data.banking.paymentsConnection.pageInfo.hasNextPage ?? false,
    }),
    search: { enabled: true },
    filters: filterDefinitions,
    sortings,
    defaultSortings,
    itemActions: getItemActions,
    itemOnClick: onRowClick,
    primaryActions: () => [
      {
        label: i18n.t('banking.payments.actions.create_bulk_payment'),
        icon: Add,
        onClick: () => {
          track(trackingEvents.accounting.payments.clickCreateBulkPayment)
          onNewPaymentAction()
        },
      },
    ],
  })

  // Extend source with nested table functionality
  // TypeScript needs help here - we're extending the source with nested table props
  // The spread operator should preserve all types from baseSource
  const source = useMemo(() => {
    return {
      ...baseSource,
      itemsWithChildren,
      childrenCount,
      fetchChildren,
      // Override itemOnClick to handle both payments and transfers
      // OneDataCollection applies itemOnClick to both parent and child rows
      itemOnClick: (item: PaymentBase | MappedTransferChild) => {
        const paymentItem: Payment = item
        if (isMappedTransferChild(paymentItem)) {
          // It's a transfer - open the sidepanel
          return () => setSelectedTransfer(paymentItem)
        }
        // For payments, return the original handler (no-op)
        const paymentBaseItem: PaymentBase = item
        return onRowClick(paymentBaseItem)
      },
    }
  }, [baseSource, itemsWithChildren, childrenCount, fetchChildren, onRowClick])

  return (
    <>
      <Box style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
        <OneDataCollection
          id="payments/v1"
          storage={{ features: ['filters', 'sortings', 'search'] }}
          fullHeight
          emptyStates={{
            'no-data': {
              title: i18n.t('banking.payments.data_table.empty_state.title'),
              description: i18n.t('banking.payments.data_table.empty_state.description_2'),
            },
          }}
          source={source}
          visualizations={[
            {
              type: 'table',
              options: {
                allowColumnHiding: true,
                allowColumnReordering: true,
                columns,
              },
            },
          ]}
        />
      </Box>
      {selectedTransfer && (
        <TransferHistorySidePanel
          transfer={selectedTransfer}
          onClose={() => setSelectedTransfer(null)}
        />
      )}
    </>
  )
}
